/**
 * AdminDashboard - Super Admin and regular admin dashboard.
 *
 * Iter 438k Phase 1 — Admin Users / Requests / Moderation / Security /
 * Shifts / Organization tabs moved into /superadmin. This page now shows the
 * Events landing for super-admins (with a banner pointing to /superadmin)
 * and the event list for regular admins.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { authHeaders } from '@/lib/authToken';
import { getToken } from '@/lib/authToken';
import { eventAPI, dashboardAPI, getErrorMessage, churchesAPI } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts';
import ConsentComplianceWidget from '@/components/consent/ConsentComplianceWidget';
import { HealthAtAGlance } from '@/components/admin/HealthAtAGlance';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Feature Components
import SuperAuditActivityCard from '@/components/SuperAuditActivityCard';
import EventScopedDashboard from '@/components/EventScopedDashboard';
import RestrictedRevenueCard from '@/components/finance/RestrictedRevenueCard';

// Icons
import {
  Calendar, Users, Settings, Plus, Loader2, Shield,
  Trash2, Eye, EyeOff, Archive, MoreVertical, Github,
  TrendingUp, DollarSign, UserCheck, Activity, UtensilsCrossed, CreditCard, Building2,
  Church, FileText, UserCog, BookOpen, Layers, Camera, Palette, CheckCircle, GraduationCap, Heart, Scale, Home, QrCode, Video, Wrench, HandCoins,
  Globe, Store, MessageCircle, Tv, Lock, Image as ImageIcon, Gavel,
  Cpu, MapPin, DoorOpen, Sparkles, Mic, Link2, Cable, HandHeart, ShieldCheck, Crown, Clock, History, ShieldAlert, Star, Radio
} from 'lucide-react';
import { isPlatformTier } from '@/lib/roleTier';

// Legacy super-admin tabs that have moved into /superadmin (iter 438k Phase 1).
const LEGACY_SUPERADMIN_TABS = new Set([
  'admins', 'requests', 'moderation', 'security-officers', 'shifts', 'organization',
]);

// Helper to safely get error message from fetch response
const getFetchErrorMessage = (err, fallback = 'An error occurred') => {
  if (!err?.detail) return fallback;
  if (typeof err.detail === 'string') return err.detail;
  if (Array.isArray(err.detail)) {
    return err.detail.map(e => e.msg || e).join(', ') || fallback;
  }
  return fallback;
};

// ============== Module Tile ==============
// Unified navigation tile: subtle card with a colored icon chip.
// Replaces the rainbow gradient cards for a cleaner, more scannable dashboard.
const ACCENT_MAP = {
  sky: 'bg-sky-500/15 text-sky-600 ring-sky-500/20 group-hover:bg-sky-500/25 dark:text-sky-300',
  emerald: 'bg-emerald-500/15 text-emerald-600 ring-emerald-500/20 group-hover:bg-emerald-500/25 dark:text-emerald-300',
  orange: 'bg-orange-500/15 text-orange-600 ring-orange-500/20 group-hover:bg-orange-500/25 dark:text-orange-300',
  violet: 'bg-violet-500/15 text-violet-600 ring-violet-500/20 group-hover:bg-violet-500/25 dark:text-violet-300',
  slate: 'bg-slate-500/15 text-slate-600 ring-slate-500/20 group-hover:bg-slate-500/25 dark:text-slate-300',
  amber: 'bg-amber-500/15 text-amber-600 ring-amber-500/20 group-hover:bg-amber-500/25 dark:text-amber-300',
  indigo: 'bg-indigo-500/15 text-indigo-600 ring-indigo-500/20 group-hover:bg-indigo-500/25 dark:text-indigo-300',
  fuchsia: 'bg-fuchsia-500/15 text-fuchsia-600 ring-fuchsia-500/20 group-hover:bg-fuchsia-500/25 dark:text-fuchsia-300',
  rose: 'bg-rose-500/15 text-rose-600 ring-rose-500/20 group-hover:bg-rose-500/25 dark:text-rose-300',
  cyan: 'bg-cyan-500/15 text-cyan-600 ring-cyan-500/20 group-hover:bg-cyan-500/25 dark:text-cyan-300',
  green: 'bg-green-500/15 text-green-600 ring-green-500/20 group-hover:bg-green-500/25 dark:text-green-300',
};

const ModuleTile = ({ icon: Icon, title, sub, to, accent = 'indigo', testId, navigate, locked = false, lockedReason = '' }) => {
  const chip = ACCENT_MAP[accent] || ACCENT_MAP.indigo;
  if (locked) {
    // Visible but inert — staff can see the module exists without being able
    // to open it or read a single figure from it.
    return (
      <div
        data-testid={testId}
        title={lockedReason}
        aria-disabled="true"
        className="text-left rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-4 flex items-start gap-3 cursor-not-allowed opacity-60 dark:bg-white/[0.02] dark:border-white/10"
      >
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg ring-1 ring-slate-200 bg-slate-100 text-slate-400">
          <Lock className="h-4 w-4" strokeWidth={2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-500 truncate dark:text-slate-300">{title}</span>
          <span className="block text-[11px] text-slate-400 mt-0.5 truncate">{lockedReason || sub}</span>
        </span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      data-testid={testId}
      className="group text-left rounded-xl border border-slate-200/80 bg-white/90 hover:border-slate-300 hover:shadow-md transition-all p-4 flex items-start gap-3 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 dark:bg-white/[0.03] dark:border-white/10 dark:hover:bg-white/[0.06]"
    >
      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ring-1 transition-colors ${chip}`}>
        <Icon className="h-4.5 w-4.5" strokeWidth={2} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900 truncate dark:text-white">{title}</span>
        <span className="block text-[11px] text-slate-500 mt-0.5 truncate dark:text-slate-400">{sub}</span>
      </span>
    </button>
  );
};

// ============== Create Event Dialog ==============
const CreateEventDialog = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    start_date: "",
    end_date: "",
    event_type: "in_person",
    meals_enabled: true,
    housing_enabled: false,
    economy_enabled: true,
    church_ids: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  // Fetch churches for selection
  const { data: churches } = useQuery({
    queryKey: ['churches-for-event'],
    queryFn: () => churchesAPI.adminList().then(r => r.data),
    enabled: open
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await eventAPI.create({
        ...form,
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
        ticket_tiers: [
          {
            id: crypto.randomUUID(),
            name: "Standard",
            description: "General admission",
            price: 0,
            capacity: 100,
            sold: 0,
            includes_meals: form.meals_enabled,
            includes_housing: false,
            initial_coins: 100,
          },
        ],
      });
      
      // Tag the event to the chosen churches / provinces
      if (form.church_ids.length > 0 && response.data?.id) {
        try {
          await eventAPI.updateOrgTags(response.data.id, form.church_ids);
        } catch (err) {
          console.error('Failed to tag churches:', err);
        }
      }
      
      toast.success("Event created!");
      onSuccess();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to create event"));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChurch = (churchId) => {
    setForm(prev => ({
      ...prev,
      church_ids: prev.church_ids.includes(churchId)
        ? prev.church_ids.filter(id => id !== churchId)
        : [...prev.church_ids, churchId]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Event</DialogTitle>
          <DialogDescription>Fill in the details for your event</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Event Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
              required
              data-testid="event-name"
            />
          </div>
          <div>
            <Label htmlFor="slug">URL Slug</Label>
            <Input
              id="slug"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              required
              data-testid="event-slug"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="event-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                required
                data-testid="event-start"
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                required
                data-testid="event-end"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="event_type">Event type</Label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="event-type-group">
              {[
                { key: "in_person", label: "In person" },
                { key: "online", label: "Online" },
                { key: "hybrid", label: "Hybrid" },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setForm({ ...form, event_type: option.key })}
                  data-testid={`event-type-${option.key}`}
                  className={`rounded-full border px-3 py-2 text-sm transition-colors ${
                    form.event_type === option.key
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              New events are saved as a <strong>draft</strong> — publish it when you
              are ready for it to appear publicly.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="meals"
                checked={form.meals_enabled}
                onCheckedChange={(c) => setForm({ ...form, meals_enabled: c })}
                data-testid="event-meals-checkbox"
              />
              <Label htmlFor="meals">Enable Meals</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="housing"
                checked={form.housing_enabled}
                onCheckedChange={(c) => setForm({ ...form, housing_enabled: c })}
                data-testid="event-housing-checkbox"
              />
              <Label htmlFor="housing">Enable Housing</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="economy"
                checked={form.economy_enabled}
                onCheckedChange={(c) => setForm({ ...form, economy_enabled: c })}
                data-testid="event-economy-checkbox"
              />
              <Label htmlFor="economy">Enable EventCoins</Label>
            </div>
          </div>
          
          {/* Church / province tagging */}
          {churches && churches.length > 0 && (
            <div>
              <Label className="mb-2 block">Tag to churches / provinces</Label>
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2" data-testid="event-tag-list">
                {churches.map(church => (
                  <div key={church.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`church-${church.id}`}
                      checked={form.church_ids.includes(church.id)}
                      onCheckedChange={() => toggleChurch(church.id)}
                      data-testid={`event-tag-checkbox-${church.id}`}
                    />
                    <Label htmlFor={`church-${church.id}`} className="flex-1 cursor-pointer text-sm">
                      {church.name}
                      <span className="text-slate-400 ml-1 text-xs">
                        {(church.organisation_type || church.level || '').replace(/_/g, ' ')}
                      </span>
                      {church.location && (
                        <span className="text-slate-500 ml-1">({church.location})</span>
                      )}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Tagged churches and provinces see this event in their workspace Events list and on their page
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading} data-testid="create-event-submit">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== Admin Dashboard ==============

// A Bishop or Overseer lands here — this is their way into their province.
const MyProvinceBanner = ({ enabled, navigate }) => {
  const { data } = useQuery({
    queryKey: ['my-offices'],
    queryFn: async () => {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/churches-hx/my-offices`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('no offices');
      return res.json();
    },
    enabled: !!enabled,
    retry: false,
  });
  const offices = data?.offices || [];
  if (!offices.length) return null;
  return (
    <Card className="border-amber-300 bg-amber-50/60" data-testid="my-province-banner">
      <CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-amber-900">
            {offices.map((o) => `${o.label} of ${o.unit_name}`).join(' · ')}
          </p>
          <p className="text-xs text-amber-800">
            {data?.reach || 0} unit(s) under your oversight — districts, assemblies and their people.
          </p>
        </div>
        <Button onClick={() => navigate('/admin/my-province')} data-testid="open-my-province-btn">
          Open My Province →
        </Button>
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check if user is super admin
  const isSuperAdmin = user?.is_super_admin || user?.permissions?.includes('super_admin') || user?.permissions?.includes('*') || user?.role === 'super_admin';

  // An admin invited onto a single event only ever sees that event. The
  // backend enforces this too (core/event_scope.py) — this keeps the UI honest.
  const assignedCount = (user?.assigned_events?.length || 0)
    + Object.keys(user?.event_roles || {}).length;
  const isPlatformAdmin = isPlatformTier(user);
  const isEventScoped = !isSuperAdmin && (
    user?.event_scope?.is_event_scoped
    ?? (user?.role === 'event_team' && assignedCount > 0)
  );

  // Legacy bookmark redirect: `/admin?tab=admins` (etc.) → `/superadmin?tab=admins`
  // Panels moved to the Super Admin Portal in iter 438k Phase 1.
  useEffect(() => {
    const legacyTab = searchParams.get('tab');
    if (legacyTab && LEGACY_SUPERADMIN_TABS.has(legacyTab) && isSuperAdmin) {
      navigate(`/superadmin?tab=${legacyTab}`, { replace: true });
    } else if (legacyTab && !LEGACY_SUPERADMIN_TABS.has(legacyTab)) {
      // Unknown / stale tab param — strip it to keep the URL clean.
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
     
  }, [isSuperAdmin]);

  // Fetch dashboard stats
  const { data: dashboardStats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardAPI.getStats().then(r => r.data),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Use filtered events list based on admin access
  const { data: events, isLoading } = useQuery({
    queryKey: ["admin-events"],
    queryFn: () => eventAPI.listForAdmin().then((r) => r.data),
  });

  // Event management functions (super admin only)
  const handleArchiveEvent = async (eventId, archive = true) => {
    try {
      await eventAPI.archive(eventId, archive);
      toast.success(archive ? 'Event archived — removed from the public pages'
        : 'Event restored as a draft');
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update event'));
    }
  };

  const handlePublishEvent = async (eventId, publish) => {
    try {
      await eventAPI.publish(eventId, publish);
      toast.success(publish ? 'Event published' : 'Event moved back to draft');
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update publication state'));
    }
  };

  const handleFeatureEvent = async (eventId, featured) => {
    try {
      await eventAPI.feature(eventId, featured);
      toast.success(featured ? 'Event marked as featured' : 'Removed from featured');
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update featured state'));
    }
  };

  const handleToggleVisibility = async (eventId, visible) => {
    try {
      await eventAPI.setVisibility(eventId, visible);
      toast.success(visible ? 'Event is now visible' : 'Event is now hidden');
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to update event'));
    }
  };

  const handleDeleteEvent = async () => {
    if (!deleteEventId) return;
    setIsDeleting(true);
    try {
      // Only the backend's own JSON counts as a delete. A 2xx that is not this
      // contract means the request never reached the API (iter 619).
      const res = await eventAPI.remove(deleteEventId);
      if (!res?.data?.message) throw new Error('unexpected response');
      toast.success('Event deleted permanently');
      await queryClient.invalidateQueries({ queryKey: ['admin-events'] });
      setDeleteEventId(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to delete event — the event was not removed'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    isEventScoped ? <EventScopedDashboard user={user} /> :
    <DashboardLayout title={t('dashboard.title', 'Admin Dashboard')}>
      <div className="space-y-6 overflow-x-hidden max-w-full">
        <MyProvinceBanner enabled={!isSuperAdmin} navigate={navigate} />
        {/* Header with admin info */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">{t('auth.welcomeBack', 'Welcome')}, {user?.first_name || user?.name || t('common.admin', 'Admin')}</h1>
            <div className="text-slate-500">
              {isSuperAdmin ? (
                <Badge className="bg-red-100 text-red-700">{t('common.superAdmin', 'Super Admin')}</Badge>
              ) : (
                <span>{t('dashboard.accessTo', 'You have access to')} {events?.length || 0} {t('events.title', 'event(s)')}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/home')}
                    className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white"
                    data-testid="open-command-center-btn">
              Command Center →
            </Button>
            {isSuperAdmin && (
              <Button onClick={() => navigate('/superadmin')} variant="outline" data-testid="open-superadmin-btn">
                Super Admin Portal
              </Button>
            )}
          </div>
        </div>

        {/* Quick Stats Cards */}
        {dashboardStats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="stat-events">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('dashboard.totalEvents', 'Total Events')}</p>
                    <p className="text-3xl font-bold">{dashboardStats.events?.total || 0}</p>
                    <p className="text-xs text-green-600">{dashboardStats.events?.active || 0} {t('common.active', 'active')}</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-registrations">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('admin.totalRegistrations', 'Total Registrations')}</p>
                    <p className="text-3xl font-bold">{dashboardStats.registrations?.total || 0}</p>
                    <p className="text-xs text-green-600">+{dashboardStats.registrations?.today || 0} today</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-checkin">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Check-in Rate</p>
                    <p className="text-3xl font-bold">{dashboardStats.registrations?.check_in_rate || 0}%</p>
                    <p className="text-xs text-slate-500">{dashboardStats.registrations?.checked_in || 0} checked in</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-revenue">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('admin.revenue', 'Total Revenue')}</p>
                    <p className="text-3xl font-bold">{dashboardStats.revenue?.formatted || '$0'}</p>
                    <p className="text-xs text-green-600">
                      <TrendingUp className="h-3 w-3 inline mr-1" />
                      {dashboardStats.registrations?.recent || 0} this week
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* iter 682 — Total Ecosystem Revenue is restricted. The figure is
              never fetched for an unauthorised browser and never shown until
              the authorised person deliberately asks for it, so it cannot
              appear on a shared screen on login. */}
          {isSuperAdmin && <RestrictedRevenueCard />}

          {/* iter-378 — Churches card. The admin reported "I created a church
              but don't see how it's connected" because this dashboard had
              zero visibility into the `churches` collection. The card links
              to the existing Hierarchy Explorer at /admin/churches/hierarchy. */}
          {dashboardStats.churches && (dashboardStats.churches.total > 0) && (
            <Card data-testid="stat-churches" className="mt-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate('/admin/churches/hierarchy')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('admin.churches', 'Churches in Hierarchy')}</p>
                    <p className="text-3xl font-bold">{dashboardStats.churches.active}</p>
                    <p className="text-xs mt-0.5">
                      <span className="text-slate-500">{dashboardStats.churches.total} total</span>
                      {dashboardStats.churches.orphans > 0 ? (
                        <span className="ml-2 text-rose-600 font-semibold">
                          · {dashboardStats.churches.orphans} unassigned — click to fix
                        </span>
                      ) : (
                        <span className="ml-2 text-emerald-600">
                          · all attached to hierarchy
                        </span>
                      )}
                    </p>
                  </div>
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                    dashboardStats.churches.orphans > 0 ? 'bg-rose-100' : 'bg-indigo-100'
                  }`}>
                    <Building2 className={`h-6 w-6 ${
                      dashboardStats.churches.orphans > 0 ? 'text-rose-600' : 'text-indigo-600'
                    }`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Consent Compliance widget */}
          <div className="mt-4">
            <ConsentComplianceWidget tenantId={useAuthStore.getState().user?.tenant_id || 'global'} />
          </div>

          {/* Registration Status Breakdown */}
          <Card data-testid="stat-registration-breakdown" className="mt-4">
            <CardContent className="pt-6">
              <h3 className="text-sm font-medium text-slate-700 mb-4">Registration Status Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{dashboardStats.registrations?.by_status?.interested || 0}</p>
                  <p className="text-xs text-blue-600">Interested</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{dashboardStats.registrations?.by_status?.pending_payment || 0}</p>
                  <p className="text-xs text-amber-600">Pending Payment</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{dashboardStats.registrations?.by_status?.registered || 0}</p>
                  <p className="text-xs text-green-600">Registered</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{dashboardStats.registrations?.by_status?.confirmed || 0}</p>
                  <p className="text-xs text-purple-600">Confirmed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
        )}

        {/* Quick Access Modules - Clean grouped grid */}
        <div className="space-y-6">
          {/* Event Operations */}
          <div>
            <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
              <Activity className="h-3.5 w-3.5" />
              Event Operations
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <ModuleTile icon={Shield} title="Security" sub="Gate & visitors" to="/admin/security" accent="sky" testId="security-dashboard-btn" navigate={navigate} />
              <ModuleTile icon={CreditCard} title="Point of Sale" sub="Orders & payments" to="/pos" accent="emerald" testId="pos-quick-access-card" navigate={navigate} />
              <ModuleTile icon={UtensilsCrossed} title="Dining" sub="Menu & kitchen" to="/admin/dining" accent="orange" testId="dining-quick-access-card" navigate={navigate} />
              <ModuleTile icon={Home} title="Housing" sub="Unified: schools + events" to="/admin/housing" accent="violet" testId="unified-housing-quick-access-card" navigate={navigate} />
              <ModuleTile icon={Camera} title="CCTV" sub="Camera management" to="/admin/cctv" accent="slate" testId="cctv-quick-access-card" navigate={navigate} />
              <ModuleTile icon={QrCode} title="Universal Scanner" sub="One QR for every scope" to="/admin/universal-scanner" accent="cyan" testId="universal-scanner-quick-access-card" navigate={navigate} />
              <ModuleTile icon={Users} title="Scope Registrations" sub="Register + check-in" to="/admin/scope-registrations" accent="indigo" testId="scope-reg-quick-access-card" navigate={navigate} />
              {isSuperAdmin && (
                <ModuleTile icon={Trash2} title="Data Cleanup" sub="Delete test data safely" to="/admin/data-cleanup" accent="rose" testId="data-cleanup-quick-access-card" navigate={navigate} />
              )}
              <ModuleTile icon={Building2} title="Ecosystem Bank" sub="Multi-currency · Double-entry" to="/admin/bank" accent="emerald" testId="bank-quick-access-card" navigate={navigate} />
              <ModuleTile icon={Video} title="The Studio" sub="Book rooms, sign out gear" to="/studio" accent="violet" testId="studio-quick-access-card-top" navigate={navigate} />
              <ModuleTile icon={Heart} title="My Campaigns" sub="Chase pledges you own" to="/my-campaigns" accent="emerald" testId="my-campaigns-quick-access-card-top" navigate={navigate} />
              <ModuleTile icon={UserCog} title="Team Management" sub="Add people · assign roles" to="/admin/teams" accent="slate" testId="team-management-quick-access-card" navigate={navigate} />
              <ModuleTile icon={Wrench} title="Facilities" sub="Inventory · Assets · Maintenance · Procurement" to="/admin/facilities" accent="indigo" testId="facilities-quick-access-card" navigate={navigate} />
            </div>
          </div>

          {isSuperAdmin && (
            <>
              {/* People & Access */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" />
                  People & Access
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Shield} title="Permissions" sub="Role × permission matrix" to="/admin/permissions" accent="emerald" testId="permissions-matrix-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={UserCog} title="Staff Roles" sub="Role definitions" to="/admin/staff-roles" accent="fuchsia" testId="staff-roles-card" navigate={navigate} />
                  <ModuleTile icon={Users} title="Ecosystem HR" sub="Unified people directory · dedupe · cross-scope · employee fields" to="/admin/ecosystem-hr" accent="indigo" testId="ecosystem-hr-card" navigate={navigate} />
                  <ModuleTile icon={TrendingUp} title="Cross-school" sub="Ecosystem-wide roll-up" to="/admin/cross-school-analytics" accent="sky" testId="cross-school-analytics-card" navigate={navigate} />
                  <ModuleTile icon={DollarSign} title="Pricing Plans" sub="Plans & scope config" to="/admin/pricing-plans" accent="emerald" testId="pricing-plans-card" navigate={navigate} />
                  <ModuleTile icon={BookOpen} title="Bible Plans" sub="Reading plan curation" to="/admin/bible-plans" accent="indigo" testId="bible-plans-card" navigate={navigate} />
                </div>
              </div>

              {/* Governance — RBAC deep tools (super-admin only) */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Gavel className="h-3.5 w-3.5" />
                  Governance
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Layers} title="Control Centre" sub="Roles, hierarchy & grants in one place" to="/admin/control-centre" accent="indigo" testId="control-centre-card" navigate={navigate} />
                  <ModuleTile icon={Shield} title="RBAC Audit" sub="Who has access where · super-admin sweep" to="/admin/governance/rbac-audit" accent="rose" testId="rbac-audit-card" navigate={navigate} />
                  <ModuleTile icon={UserCog} title="Role Builder" sub="Design custom roles · clone · bulk apply" to="/admin/governance/role-builder" accent="fuchsia" testId="role-builder-card" navigate={navigate} />
                  <ModuleTile icon={Users} title="Team Permissions" sub="Capabilities per team member" to="/admin/governance/team-permissions" accent="emerald" testId="team-permissions-card" navigate={navigate} />
                  <ModuleTile icon={ShieldAlert} title="Denied Attempts" sub="What was blocked · the grant that fixes it" to="/admin/governance/denied-attempts" accent="amber" testId="denied-attempts-card" navigate={navigate} />
                  <ModuleTile icon={Building2} title="Platform Portal" sub="Tenants · plans · applications · platform settings" to="/admin/platform" accent="slate" testId="platform-portal-card" navigate={navigate} />
                </div>
              </div>

              {/* Institutions */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5" />
                  Institutions
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Church} title="Churches" sub="Directory" to="/admin/churches" accent="cyan" testId="churches-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Calendar} title="Calendar" sub="Everything dated, one view" to="/admin/calendar" accent="indigo" testId="calendar-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Building2} title="Institutions" sub="Hospitals, Clinics" to="/admin/institutions" accent="rose" testId="institutions-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={GraduationCap} title="Schools" sub="Tenant schools (with library)" to="/admin/schools" accent="violet" testId="schools-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Cpu} title="Computer Lab" sub="Lab PCs · sessions · availability" to="/admin/lab" accent="sky" testId="lab-pcs-card" navigate={navigate} />
                  <ModuleTile icon={Calendar} title="Appointments" sub="Pastoral & clinic bookings" to="/admin/appointments" accent="teal" testId="appointments-card" navigate={navigate} />
                  <ModuleTile icon={Church} title="Church Site" sub="Per-church public page content" to="/admin/church-site" accent="fuchsia" testId="church-site-card" navigate={navigate} />
                </div>
              </div>

              {/* Finance & Outreach */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <DollarSign className="h-3.5 w-3.5" />
                  Finance & Outreach
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Scale} title="Unified Ledger" sub="All revenue, one view" to="/admin/ledger" accent="fuchsia" testId="ledger-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={HandCoins} title="Giving" sub="Confirmed vs pending · church & campaign" to="/admin/giving" accent="emerald" testId="giving-dashboard-card" navigate={navigate} />
                  <ModuleTile icon={Clock} title="Approvals" sub="Expenses & transfers awaiting sign-off" to="/admin/approvals" accent="amber" testId="approvals-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={History} title="Role Changes" sub="Who granted what, and when" to="/admin/role-changes" accent="indigo" testId="role-changes-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={DollarSign} title="Revenue Dashboard" sub="All income wallets, one chart" to="/admin/finance/revenue" accent="emerald" testId="revenue-dashboard-card" navigate={navigate} locked={!isPlatformAdmin} lockedReason="Platform admins only" />
                  <ModuleTile icon={DollarSign} title="Finance" sub="Tithes, offerings & expenses" to="/admin/finance" accent="green" testId="finance-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={TrendingUp} title="Campaigns" sub="Fundraising & recurring" to="/admin/campaigns" accent="emerald" testId="campaigns-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="ABMHC Campaigns" sub="Donations · pledges · donor stewardship" to="/admin/donations" accent="orange" testId="donations-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="Donor Timeline" sub="Stewardship at a glance" to="/admin/donor-timeline" accent="rose" testId="donor-timeline-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="Donation Portal" sub="Public giving portal · progress · appeals" to="/donate" accent="amber" testId="donation-portal-card" navigate={navigate} />
                  <ModuleTile icon={FileText} title="Financial Reports" sub="P&L · balances · exportable statements" to="/admin/finance/reports" accent="green" testId="financial-reports-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="Donation Pages" sub="Build & publish giving pages" to="/admin/donation-pages" accent="orange" testId="donation-pages-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="Tributes" sub="In-memory & honour gifts" to="/admin/tributes" accent="rose" testId="tributes-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="Prayer Requests" sub="Incoming requests & follow-up" to="/admin/prayer-requests" accent="violet" testId="prayer-requests-card" navigate={navigate} />
                </div>
              </div>

              {/* Studio & Operations */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Video className="h-3.5 w-3.5" />
                  Studio & Operations
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Video} title="The Studio" sub="Book rooms, sign out gear" to="/studio" accent="violet" testId="studio-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="My Campaigns" sub="Chase pledges you own" to="/my-campaigns" accent="emerald" testId="my-campaigns-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={CreditCard} title="Standalone POS" sub="Till without an event" to="/admin/pos" accent="emerald" testId="standalone-pos-card" navigate={navigate} />
                  <ModuleTile icon={Home} title="Housing v2" sub="Next-gen room board" to="/admin/housing/v2" accent="teal" testId="housing-v2-card" navigate={navigate} />
                  <ModuleTile icon={DoorOpen} title="Gate Pass" sub="Vehicle & visitor passes" to="/admin/gate-pass" accent="sky" testId="gate-pass-card" navigate={navigate} />
                  <ModuleTile icon={QrCode} title="QR Badges" sub="Print & reissue delegate badges" to="/admin/qr-badges" accent="cyan" testId="qr-badges-card" navigate={navigate} />
                  <ModuleTile icon={Mic} title="Transcription Jobs" sub="Audio → text queue & status" to="/admin/transcription" accent="indigo" testId="transcription-card" navigate={navigate} />
                  <ModuleTile icon={Tv} title="Faith Works TV Team" sub="Channel crew & roles" to="/admin/fwtv/team" accent="rose" testId="fwtv-team-card" navigate={navigate} />
                  <ModuleTile icon={Store} title="Bookshop Team" sub="Shop staff & till access" to="/admin/bookshop/team" accent="amber" testId="bookshop-team-card" navigate={navigate} />
                </div>
              </div>

              {/* Community & Learning */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5" />
                  Community & Learning
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Sparkles} title="AI Tutor" sub="Study companion for students" to="/ai-tutor" accent="violet" testId="ai-tutor-card" navigate={navigate} />
                  <ModuleTile icon={GraduationCap} title="Alumni Portal" sub="Graduates, reunions & giving" to="/alumni-portal" accent="indigo" testId="alumni-portal-card" navigate={navigate} />
                </div>
              </div>

              {/* Content & Storefronts */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Palette className="h-3.5 w-3.5" />
                  Content & Storefronts
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Globe} title="Public Website" sub="Main landing page" to="/admin/landing-editor" accent="amber" testId="landing-editor-card" navigate={navigate} />
                  <ModuleTile icon={Layers} title="Module Landings" sub="Bible / Events / Giving / Churches" to="/admin/module-landing-pages" accent="indigo" testId="module-landing-card" navigate={navigate} />
                  <ModuleTile icon={FileText} title="CMS Pages" sub="Custom page builder" to="/admin/cms" accent="rose" testId="cms-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Store} title="Ministry Shop" sub="Bibles · Books · Study guides · Resources · Merchandise" to="/admin/shop" accent="emerald" testId="shop-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Tv} title="Faith Works TV" sub="CMS · Live · VOD · Podcast RSS · Transcription · Prayer · Analytics" to="/admin/fwtv" accent="indigo" testId="fwtv-quick-access-card" navigate={navigate} />
                  <ModuleTile icon={Heart} title="ABMHC Site" sub="Hope Is Rising · Hero · Stats · Milestones · Pages · Embeds" to="/admin/abmhc/onecom" accent="amber" testId="abmhc-onecom-card" navigate={navigate} />
                  <ModuleTile icon={Video} title="ABMHC Featured Media" sub="Video · CTAs · Analytics · Publish workflow" to="/admin/abmhc/featured-media" accent="rose" testId="abmhc-fm-card" navigate={navigate} />
                  <ModuleTile icon={GraduationCap} title="FHBI Site" sub="Institute · Hero · Tiers · Events · Pages · Embeds" to="/admin/fhbf/onecom" accent="indigo" testId="fhbf-onecom-card" navigate={navigate} />
                  <ModuleTile icon={Link2} title="Embeds Health" sub="Every embed · last render · failures" to="/admin/embeds" accent="cyan" testId="embeds-health-card" navigate={navigate} />
                </div>
              </div>

              {/* Restricted · Super Admin */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5" />
                  Restricted · Super Admin
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={BookOpen} title="Bible Admin" sub="Translations · imports · plan curation" to="/admin/bible" accent="indigo" testId="bible-admin-card" navigate={navigate} />
                  <ModuleTile icon={MapPin} title="Churches Map" sub="Every assembly plotted" to="/admin/churches/map" accent="cyan" testId="churches-map-card" navigate={navigate} />
                  <ModuleTile icon={Globe} title="Country Directory" sub="National office per country · assembly counts" to="/admin/churches/country-directory" accent="teal" testId="country-directory-card" navigate={navigate} />
                  <ModuleTile icon={HandHeart} title="Affiliated Ministries" sub="Independent works · applications · fees" to="/admin/affiliates" accent="amber" testId="affiliated-ministries-card" navigate={navigate} />
                  <ModuleTile icon={ShieldCheck} title="Church Roles" sub="Custom roles & permissions for members" to="/admin/church-roles" accent="emerald" testId="church-roles-card" navigate={navigate} />
                  <ModuleTile icon={Crown} title="Bishops & Overseers" sub="Provincial oversight · appointments" to="/admin/overseers" accent="amber" testId="overseers-card" navigate={navigate} />
                  <ModuleTile icon={ShieldCheck} title="Directory Sharing" sub="Who outside the church may be found" to="/admin/directory-sharing" accent="indigo" testId="directory-sharing-card" navigate={navigate} />
                  <ModuleTile icon={ShieldCheck} title="Hierarchy Roles" sub="What each office may reach" to="/admin/hierarchy-roles" accent="indigo" testId="hierarchy-roles-card" navigate={navigate} />
                  <ModuleTile icon={Users} title="Duplicate Rolls" sub="Same person entered twice · merge" to="/admin/duplicate-rolls" accent="rose" testId="duplicate-rolls-card" navigate={navigate} />
                  <ModuleTile icon={Cable} title="School Wiring" sub="Wire a tenant's modules & feeds" to="/admin/school-wiring" accent="slate" testId="school-wiring-card" navigate={navigate} />
                </div>
              </div>

              {/* System */}
              <div>
                <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-2">
                  <Settings className="h-3.5 w-3.5" />
                  System
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  <ModuleTile icon={Settings} title="Settings" sub="All system config (one page)" to="/admin/settings" accent="indigo" testId="system-settings-card" navigate={navigate} />
                  <ModuleTile icon={ImageIcon} title="Site Appearance" sub="Homepage hero image · video · words" to="/admin/heroes" accent="amber" testId="site-appearance-card" navigate={navigate} />
                  <ModuleTile icon={Home} title="Housing Staff Portal" sub="Room staff sign-in & daily board" to="/housing/staff" accent="teal" testId="housing-staff-portal-card" navigate={navigate} />
                  <ModuleTile icon={UtensilsCrossed} title="Dining POS Portal" sub="Kitchen & till staff dashboard" to="/dining/pos" accent="orange" testId="dining-pos-portal-card" navigate={navigate} />
                  <ModuleTile icon={Shield} title="RBAC Audit" sub="Who can access what" to="/admin/governance/rbac-audit" accent="rose" testId="rbac-audit-system-card" navigate={navigate} />
                  <ModuleTile icon={Globe} title="Tenant Domains" sub="CORS allow-list · production hostnames" to="/admin/tenant-domains" accent="emerald" testId="tenant-domains-card" navigate={navigate} />
                  <ModuleTile icon={MessageCircle} title="WhatsApp" sub="OpenWA · templates · logs" to="/admin/whatsapp" accent="emerald" testId="whatsapp-admin-card" navigate={navigate} />
                  <ModuleTile icon={Radio} title="SMS Gateways" sub="own SIM · own bill · per organisation" to="/admin/sms" accent="sky" testId="sms-gateways-card" navigate={navigate} />
                  <ModuleTile icon={Github} title="GitHub" sub="Code sync" to="/admin/github" accent="slate" testId="github-card" navigate={navigate} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Recent super-admin activity widget (super admin only) */}
        {isSuperAdmin && (
          <div className="mt-6">
            <SuperAuditActivityCard />
          </div>
        )}

        {/* Health at a glance — payments / social drains / rentals-expiring */}
        {isSuperAdmin && (
          <div className="mt-6">
            <HealthAtAGlance />
          </div>
        )}

        {/* Super Admin: Events landing (other panels moved to /superadmin — iter 438k Phase 1) */}
        {isSuperAdmin && (
          <>
            {/* Quick link banner to sibling super-admin panels */}
            <div className="mt-6 mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900" data-testid="superadmin-panels-hint">
              <Shield className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">Platform administration moved:</span>
              <span className="text-indigo-800/80">Admin Users, Requests, Moderation, Security, Shifts &amp; Organization now live in the Super Admin Portal.</span>
              <div className="ml-auto flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-indigo-700 hover:bg-indigo-100"
                  onClick={() => navigate('/home')}
                  data-testid="open-home-inline-btn"
                >
                  <Home className="h-4 w-4 mr-1" /> Home
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  onClick={() => navigate('/superadmin')}
                  data-testid="open-superadmin-inline-btn"
                >
                  Open Super Admin Portal →
                </Button>
              </div>
            </div>

            {/* Create Event quick action */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Button onClick={() => setShowCreateEvent(true)} data-testid="create-event-btn">
                <Plus className="mr-2 h-4 w-4" /> Create Event
              </Button>
            </div>

            {/* Events Grid */}
            <div>
              <h2 className="text-lg font-semibold mb-4">All Events</h2>
              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48" />)}
                </div>
              ) : events?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {events.map((event) => (
                    <Card key={event.id} className={`hover:shadow-md transition-shadow ${event.is_archived ? 'opacity-60' : ''}`}>
                      <div className="h-2" style={{ backgroundColor: event.branding?.primary_color || "#3B82F6" }} />
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle 
                            className="text-lg cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/admin/event/${event.id}`)}
                          >
                            {event.name}
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={event.status === 'published' ? 'default' : 'outline'}
                              className="text-xs"
                              data-testid={`event-status-badge-${event.id}`}
                            >
                              {event.status === 'published' ? 'Published' : 'Draft'}
                            </Badge>
                            {event.is_featured && (
                              <Badge className="text-xs bg-amber-500 hover:bg-amber-500"
                                     data-testid={`event-featured-badge-${event.id}`}>
                                Featured
                              </Badge>
                            )}
                            {event.event_type && event.event_type !== 'in_person' && (
                              <Badge variant="secondary" className="text-xs">
                                {event.event_type === 'online' ? 'Online' : 'Hybrid'}
                              </Badge>
                            )}
                            {event.is_archived && (
                              <Badge variant="secondary" className="text-xs">Archived</Badge>
                            )}
                            {event.is_visible === false && (
                              <Badge variant="outline" className="text-xs">Hidden</Badge>
                            )}
                            {event.status === 'blocked' && (
                              <Badge variant="destructive" className="text-xs">Blocked</Badge>
                            )}
                            {/* Super Admin Actions Menu */}
                            {isSuperAdmin && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                                          data-testid={`event-kebab-${event.id}`}>
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => navigate(`/admin/event/${event.id}`)}>
                                    <Settings className="h-4 w-4 mr-2" />
                                    Manage Event
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handlePublishEvent(event.id, event.status !== 'published')}
                                    data-testid={`event-publish-toggle-${event.id}`}
                                  >
                                    {event.status === 'published' ? (
                                      <>
                                        <EyeOff className="h-4 w-4 mr-2" />
                                        Unpublish (back to draft)
                                      </>
                                    ) : (
                                      <>
                                        <Eye className="h-4 w-4 mr-2" />
                                        Publish Event
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleFeatureEvent(event.id, !event.is_featured)}
                                    data-testid={`event-feature-toggle-${event.id}`}
                                  >
                                    <Star className="h-4 w-4 mr-2" />
                                    {event.is_featured ? 'Remove from featured' : 'Mark as featured'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleToggleVisibility(event.id, event.is_visible === false)}
                                    data-testid={`event-visibility-toggle-${event.id}`}>
                                    {event.is_visible === false ? (
                                      <>
                                        <Eye className="h-4 w-4 mr-2" />
                                        Show Event
                                      </>
                                    ) : (
                                      <>
                                        <EyeOff className="h-4 w-4 mr-2" />
                                        Hide Event
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleArchiveEvent(event.id, !event.is_archived)}
                                    data-testid={`event-archive-toggle-${event.id}`}>
                                    <Archive className="h-4 w-4 mr-2" />
                                    {event.is_archived ? 'Restore Event' : 'Archive Event'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-red-600 focus:text-red-600"
                                    data-testid={`event-delete-${event.id}`}
                                    onClick={() => setDeleteEventId(event.id)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Permanently
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                        <CardDescription>
                          {new Date(event.start_date).toLocaleDateString()}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="cursor-pointer" onClick={() => navigate(`/admin/event/${event.id}`)}>
                        <div className="flex gap-4 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {event.delegate_count || 0} registered
                          </div>
                          {event.checked_in_count > 0 && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              {event.checked_in_count} checked in
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600">No events yet</p>
                    <Button className="mt-4" onClick={() => setShowCreateEvent(true)}>
                      Create Your First Event
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}

        {/* Non-Super Admin View */}
        {!isSuperAdmin && (
          <>
            {/* Events Grid */}
            <div>
              <h2 className="text-lg font-semibold mb-4">Your Events</h2>
              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48" />)}
                </div>
              ) : events?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {events.map((event) => (
                    <Card key={event.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/admin/event/${event.id}`)}>
                      <div className="h-2" style={{ backgroundColor: event.branding?.primary_color || "#3B82F6" }} />
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{event.name}</CardTitle>
                          {event.my_role && (
                            <Badge variant="outline" className="text-xs">
                              {event.my_role.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>
                        <CardDescription>
                          {new Date(event.start_date).toLocaleDateString()}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex gap-4 text-sm text-slate-600">
                          <div className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {event.ticket_tiers?.reduce((acc, t) => acc + t.sold, 0) || 0} registered
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600">No events assigned to you</p>
                    <p className="text-sm text-slate-400 mt-2">
                      Contact a Super Admin to get access to events
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Event Dialog */}
      {isSuperAdmin && (
        <CreateEventDialog
          open={showCreateEvent}
          onClose={() => setShowCreateEvent(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["admin-events"] });
            setShowCreateEvent(false);
          }}
        />
      )}

      {/* Delete Event Confirmation Dialog */}
      <AlertDialog open={!!deleteEventId} onOpenChange={() => setDeleteEventId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Event Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the event and all associated data including:
            </AlertDialogDescription>
            <ul className="list-disc list-inside mt-2 text-sm text-muted-foreground">
              <li>All registered delegates</li>
              <li>Ticket tiers and sales</li>
              <li>Sessions and speakers</li>
              <li>Sponsors and exhibitors</li>
              <li>Surveys, prizes, and certificates</li>
              <li>All other event data</li>
            </ul>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteEvent}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default AdminDashboard;
